import * as React from 'react';

import type {User as UserFragment} from 'nutshell-graphql-types';

import {ReauthenticateModal} from '../../auth/reauthenticate-modal';
import {disableUserMfa, disableMfa, getMfa, getUserMfa} from '../../auth/utils';

import {MFASetupEmptyState} from './mfa-setup-empty-state';
import {MfaSetupEnabled} from './mfa-setup-enabled';
import type {GetMfaApiResponse} from './types';

type Props = {
    user: UserFragment | null | undefined;
    isLoadingUser: boolean;
};

export function MFASetup(props: Props) {
    const [mfaMethod, setMfaMethod] = React.useState<any>(null);
    const [mfaValue, setMfaValue] = React.useState('');
    const [isLoading, setIsLoading] = React.useState(true);
    const [mfaEnforced, setMfaEnforced] = React.useState(false);
    const [isPromptReauthenticate, setIsPromptReauthenticate] = React.useState(false);

    const handleMfa = (result: GetMfaApiResponse) => {
        setMfaMethod(result.result.type);
        setMfaEnforced(result.result.isEnforced);

        if (result.result.value) {
            setMfaValue(result.result.value);
        }

        setIsLoading(false);
    };

    const getMfaStatus = () => {
        setIsLoading(true);

        if (props.user) {
            // User supplied, set MFA of given user
            getUserMfa({userId: props.user.id}).then(handleMfa);
        } else {
            // User not supplied, set own MFA
            getMfa().then(handleMfa);
        }
    };

    React.useEffect(() => {
        getMfaStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.user]);

    if (isLoading || props.isLoadingUser) {
        return null;
    }

    const attemptDisableMfa = (userId?: string) => {
        if (userId) {
            disableUserMfa({userId: userId})
                .then(() => {
                    getMfaStatus();
                    setIsPromptReauthenticate(false);
                })
                .catch(() => {
                    setIsPromptReauthenticate(true);
                });
        } else {
            disableMfa({})
                .then(() => {
                    getMfaStatus();
                    setIsPromptReauthenticate(false);
                })
                .catch(() => {
                    setIsPromptReauthenticate(true);
                });
        }
    };

    return (
        <>
            {mfaMethod ? (
                <MfaSetupEnabled
                    user={props.user}
                    mfaMethod={mfaMethod}
                    mfaValue={mfaValue}
                    mfaEnforced={mfaEnforced}
                    attemptDisableMfa={attemptDisableMfa}
                />
            ) : (
                <MFASetupEmptyState user={props.user} />
            )}

            {isPromptReauthenticate ? (
                <ReauthenticateModal
                    attemptDisableMfa={attemptDisableMfa}
                    setIsPromptReauthenticate={setIsPromptReauthenticate}
                    userId={props.user ? props.user.id : undefined}
                />
            ) : undefined}
        </>
    );
}
